import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  runTransaction
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Cliente, Produto, Reserva, ApiResponse } from '../types';

// Função para verificar se CPF já existe
export const checkCPFExists = async (cpf: string, ignoreId?: string): Promise<boolean> => {
  try {
    const cleanCPF = cpf.replace(/\D/g, ''); // Remove pontuação
    const q = query(collection(db, "clientes"), where("cpf", "==", cleanCPF));
    const querySnapshot = await getDocs(q);

    if (ignoreId) {
      // Ignora o próprio cliente se estiver editando
      return querySnapshot.docs.some(doc => doc.id !== ignoreId);
    }

    return !querySnapshot.empty; // true se já existe
  } catch (error) {
    console.error('Erro ao verificar CPF:', error);
    return false;
  }
};

// Adicionar alias para compatibilidade
export const isCPFRegistered = checkCPFExists;

// NOVAS CONSULTAS FILTRADAS POR USUÁRIO
export const getClientesByUserPaginated = async (
  userId: string,
  searchTerm: string = '',
  pageLimit: number = 10,
  lastDoc?: any
): Promise<ApiResponse<{ items: Cliente[], lastDoc: any, hasMore: boolean }>> => {
  try {
    // Busca na subcoleção: usuarios/{userId}/clientes
    let q = query(
      collection(db, `usuarios/${userId}/clientes`),
      orderBy('nome'),
      limit(pageLimit + 1) // +1 para verificar se há mais páginas
    );

    if (lastDoc) q = query(q, startAfter(lastDoc));

    // Filtro simples no Firestore (por prefixo de nome)
    if (searchTerm) {
      q = query(
        q,
        where('nome', '>=', searchTerm),
        where('nome', '<=', searchTerm + '\uf8ff')
      );
    }

    const snap = await getDocs(q);
    const docs = snap.docs;
    const hasMore = docs.length > pageLimit;

    const items = docs.slice(0, pageLimit).map(d => ({ id: d.id, ...d.data() } as Cliente));

    return { success: true, data: { items, lastDoc: hasMore ? docs[pageLimit - 1] : null, hasMore } };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};


// Cache manual para produtos
const getProdutosFromCache = (userId: string): Produto[] | null => {
  try {
    const cachedData = sessionStorage.getItem(`produtos_cache_${userId}`);
    const timestamp = sessionStorage.getItem(`produtos_cache_timestamp_${userId}`);

    if (cachedData && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cachedData);
      }
    }
  } catch (e) {
    // Silently handle cache errors
  }
  return null;
};

const setProdutosInCache = (userId: string, data: Produto[]) => {
  try {
    sessionStorage.setItem(`produtos_cache_${userId}`, JSON.stringify(data));
    sessionStorage.setItem(`produtos_cache_timestamp_${userId}`, Date.now().toString());
  } catch (e) {
    // Silently handle cache errors
  }
};

const clearProdutosCache = (userId: string) => {
  try {
    // Cache usado no serviço
    sessionStorage.removeItem(`produtos_cache_${userId}`);
    sessionStorage.removeItem(`produtos_cache_timestamp_${userId}`);

    // Cache usado em telas de gestão
    sessionStorage.removeItem(`produtos_${userId}`);
    sessionStorage.removeItem(`produtos_${userId}_time`);
  } catch (e) {
    // Silently handle cache errors
  }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Função para buscar TODOS os produtos (sem paginação) - usado em Dashboard/Hooks
export const getProdutos = async (userId: string): Promise<ApiResponse<Produto[]>> => {
  try {
    let produtos: Produto[] = [];

    // Tentar buscar do cache primeiro
    const cachedProdutos = getProdutosFromCache(userId);

    if (cachedProdutos) {
      produtos = cachedProdutos;
    } else {
      // Buscar do servidor se não tiver no cache
      const q = query(collection(db, 'produtos'), where('userId', '==', userId));
      const snap = await getDocs(q);
      produtos = snap.docs.map(d => ({ id: d.id, ...d.data() } as Produto));

      // Salvar no cache
      setProdutosInCache(userId, produtos);
    }

    return { success: true, data: produtos };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Alias para compatibilidade
export const getProdutosByUser = getProdutos;

// Função paginada para produtos
export const getProdutosByUserPaginated = async (
  userId: string,
  pageLimit: number = 10,
  lastDoc?: any,
  filters: {
    codigo?: string;
    titulo?: string;
    tamanho?: string;
    cor?: string;
  } = {}
): Promise<ApiResponse<{ items: Produto[]; lastDoc: any; hasMore: boolean }>> => {
  try {
    // Busca na coleção raiz produtos, filtrando por userId
    const constraints: any[] = [where('userId', '==', userId)];

    // Verifica se há filtros ativos
    const hasFilters = filters.codigo || filters.titulo || filters.tamanho || filters.cor;

    constraints.push(orderBy('codigoProduto', 'asc'));

    const fetchLimit = hasFilters ? 1000 : pageLimit + 1;
    constraints.push(limit(fetchLimit));

    if (lastDoc) constraints.push(startAfter(lastDoc));

    const q = query(collection(db, 'produtos'),
      ...constraints
    );

    const snap = await getDocs(q);
    let docs = snap.docs.map(d => ({
      doc: d,
      data: { id: d.id, ...d.data() } as Produto
    }));

    // Aplica filtros case-insensitive no cliente
    if (hasFilters) {
      docs = docs.filter(({ data }) => {
        let match = true;

        if (filters.codigo) {
          const codigo = (data.codigoProduto || '').toLowerCase();
          const filterCodigo = filters.codigo.toLowerCase();
          match = match && codigo.includes(filterCodigo);
        }

        if (filters.titulo) {
          const titulo = (data.titulo || '').toLowerCase();
          const filterTitulo = filters.titulo.toLowerCase();
          match = match && titulo.includes(filterTitulo);
        }

        if (filters.tamanho) {
          const tamanho = (data.tamanho || '').toLowerCase();
          const filterTamanho = filters.tamanho.toLowerCase();
          match = match && tamanho.includes(filterTamanho);
        }

        if (filters.cor) {
          const cor = (data.cor || '').toLowerCase();
          const filterCor = filters.cor.toLowerCase();
          match = match && cor.includes(filterCor);
        }

        return match;
      });
    }

    const hasMore = docs.length > pageLimit;
    const items = docs.slice(0, pageLimit).map(d => d.data);
    const newLastDoc = hasMore ? docs[pageLimit - 1].doc : null;

    return {
      success: true,
      data: {
        items,
        lastDoc: newLastDoc,
        hasMore
      }
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Função para buscar TODAS as reservas (sem paginação) - usado em Dashboard/Hooks
export const getReservas = async (userId: string): Promise<ApiResponse<Reserva[]>> => {
  try {
    // Busca na coleção raiz reservas, filtrando por userId
    const q = query(collection(db, 'reservas'), where('userId', '==', userId));
    const snap = await getDocs(q);
    const reservas = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      // Converter createdAt do Firebase Timestamp para Date
      createdAt: d.data().createdAt?.toDate() || new Date()
    } as Reserva));
    return { success: true, data: reservas };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Alias para compatibilidade
export const getReservasByUser = getReservas;

// Nova função com paginação
export const getReservasByUserPaginated = async (
  userId: string,
  pageLimit: number = 10,
  lastDoc?: any
): Promise<ApiResponse<{ items: Reserva[]; lastDoc: any; hasMore: boolean }>> => {
  try {
    // Busca na coleção raiz reservas, filtrando por userId
    let q = query(
      collection(db, 'reservas'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(pageLimit + 1) // +1 para verificar se há mais páginas
    );

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snap = await getDocs(q);
    const docs = snap.docs;
    const hasMore = docs.length > pageLimit;

    // Remove o último documento se há mais páginas
    const items = docs.slice(0, pageLimit).map(d => ({
      id: d.id,
      ...d.data()
    } as Reserva));

    return {
      success: true,
      data: {
        items,
        lastDoc: hasMore ? docs[pageLimit - 1] : null,
        hasMore
      }
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Clientes
export const addCliente = async (cliente: Omit<Cliente, 'id' | 'createdAt'>): Promise<ApiResponse<string>> => {
  try {
    // Verificar se CPF já existe
    const cpfExists = await checkCPFExists(cliente.cpf);
    if (cpfExists) {
      return { success: false, error: 'Este CPF já está cadastrado!' };
    }

    const docRef = await addDoc(collection(db, 'clientes'), {
      ...cliente,
      createdAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Função para buscar TODOS os clientes (sem paginação) - usado em Dashboard/Hooks
export const getClientes = async (userId: string): Promise<ApiResponse<Cliente[]>> => {
  try {
    // Busca na subcoleção: usuarios/{userId}/clientes
    const querySnapshot = await getDocs(collection(db, `usuarios/${userId}/clientes`));
    const clientes = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Cliente));
    return { success: true, data: clientes };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateCliente = async (id: string, cliente: Partial<Cliente>): Promise<ApiResponse<void>> => {
  try {
    // Se estiver atualizando o CPF, verificar se já existe
    if (cliente.cpf) {
      const cpfExists = await checkCPFExists(cliente.cpf, id);
      if (cpfExists) {
        return { success: false, error: 'Este CPF já está cadastrado!' };
      }
    }

    await updateDoc(doc(db, 'clientes', id), cliente);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteCliente = async (id: string): Promise<ApiResponse<void>> => {
  try {
    await deleteDoc(doc(db, 'clientes', id));
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Gera um código único e sequencial para o produto
 * Formato: Apenas números com 4 dígitos (ex: 0001, 0002, 0003)
 * Usa transação do Firestore para garantir que o código seja único mesmo em cadastros simultâneos
 * 
 * O contador é armazenado por usuário na coleção 'produto_counters'
 * Cada usuário tem sua própria sequência numérica independente
 * 
 * IMPORTANTE: Para reiniciar o contador:
 * 1. Acesse o Firestore Console
 * 2. Navegue até a coleção 'produto_counters'
 * 3. Encontre o documento com ID do seu usuário (ex: abc123xyz)
 * 4. Altere o campo 'lastNumber' para 0 ou delete o documento
 * 
 * @param userId - ID do usuário proprietário
 * @returns Código único gerado (ex: '0001', '0002', '0123')
 */
export const generateCodigoProduto = async (userId: string): Promise<ApiResponse<string>> => {
  try {
    const result = await runTransaction(db, async (transaction) => {
      // Referência para o contador do usuário
      // Usa o próprio userId como ID do documento
      const counterRef = doc(db, 'produto_counters', userId);

      // Ler o contador atual dentro da transação
      const counterDoc = await transaction.get(counterRef);

      let currentNumber: number;

      if (counterDoc.exists()) {
        // Se o contador já existe, incrementa o número
        currentNumber = counterDoc.data().lastNumber + 1;
        transaction.update(counterRef, {
          lastNumber: currentNumber,
          updatedAt: new Date()
        });
      } else {
        // Se não existe, cria o contador iniciando em 1
        currentNumber = 1;
        transaction.set(counterRef, {
          lastNumber: currentNumber,
          userId: userId,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // Formatar o código: apenas número com 4 dígitos (ex: 0001, 0002, 0123)
      const codigoProduto = currentNumber.toString().padStart(4, '0');

      return codigoProduto;
    });

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Erro ao gerar código do produto:', error);
    return { success: false, error: error.message || 'Erro ao gerar código do produto' };
  }
};

// Produtos
export const addProduto = async (produto: Omit<Produto, 'id' | 'createdAt'>): Promise<ApiResponse<string>> => {
  try {
    const userId = produto.userId;

    // Gerar código único para o produto (apenas numérico sequencial)
    const codigoResult = await generateCodigoProduto(userId);
    if (!codigoResult.success) {
      return { success: false, error: codigoResult.error || 'Erro ao gerar código do produto' };
    }

    const codigoProduto = codigoResult.data;

    // Adicionar o produto com o código único
    const docRef = await addDoc(collection(db, 'produtos'), {
      ...produto,
      codigoProduto, // Código único e permanente (ex: 0001, 0002, 0003)
      createdAt: new Date()
    });

    clearProdutosCache(userId);

    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateProduto = async (id: string, produto: Partial<Produto>): Promise<ApiResponse<void>> => {
  try {
    const userId = produto.userId || auth.currentUser?.uid;

    // Remove codigoProduto do objeto de atualização para garantir que nunca seja alterado
    // O código do produto é gerado apenas uma vez no cadastro e deve ser permanente
    const { codigoProduto, ...produtoSemCodigo } = produto;

    await updateDoc(doc(db, 'produtos', id), produtoSemCodigo);
    if (userId) {
      clearProdutosCache(userId);
    }
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteProduto = async (id: string): Promise<ApiResponse<void>> => {
  try {
    await deleteDoc(doc(db, 'produtos', id));
    const userId = auth.currentUser?.uid;
    if (userId) {
      clearProdutosCache(userId);
    }
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Função para verificar disponibilidade de estoque
export const verificarDisponibilidadeEstoque = async (produtos: any[], userId: string): Promise<{ disponivel: boolean; produtosIndisponiveis: string[] }> => {
  try {
    const produtosResult = await getProdutosByUser(userId);
    if (!produtosResult.success || !produtosResult.data) {
      return { disponivel: false, produtosIndisponiveis: [] };
    }

    const produtosDisponiveis = produtosResult.data;
    const produtosIndisponiveis: string[] = [];

    for (const produtoReserva of produtos) {
      const produto = produtosDisponiveis.find(p => p.id === produtoReserva.produtoId);
      if (!produto) {
        produtosIndisponiveis.push(produtoReserva.produtoTitulo);
        continue;
      }

      if (produto.quantidade < produtoReserva.quantidade) {
        produtosIndisponiveis.push(`${produtoReserva.produtoTitulo} (disponível: ${produto.quantidade}, solicitado: ${produtoReserva.quantidade})`);
      }
    }

    return {
      disponivel: produtosIndisponiveis.length === 0,
      produtosIndisponiveis
    };
  } catch (error) {
    console.error('Erro ao verificar disponibilidade:', error);
    return { disponivel: false, produtosIndisponiveis: [] };
  }
};

// Função para atualizar quantidade de produtos no estoque
export const atualizarEstoqueProdutos = async (produtos: any[], operacao: 'reservar' | 'devolver', userId: string): Promise<ApiResponse<void>> => {
  try {
    const produtosResult = await getProdutosByUser(userId);
    if (!produtosResult.success || !produtosResult.data) {
      return { success: false, error: 'Erro ao carregar produtos' };
    }

    const produtosDisponiveis = produtosResult.data;
    const multiplicador = operacao === 'reservar' ? -1 : 1;

    for (const produtoReserva of produtos) {
      const produto = produtosDisponiveis.find(p => p.id === produtoReserva.produtoId);
      if (produto) {
        const novaQuantidade = produto.quantidade + (produtoReserva.quantidade * multiplicador);
        await updateProduto(produto.id!, { quantidade: Math.max(0, novaQuantidade) });
      }
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Reservas
export const addReserva = async (reserva: Omit<Reserva, 'id' | 'createdAt' | 'clienteNome'>): Promise<ApiResponse<string>> => {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      return { success: false, error: 'Usuário não autenticado' };
    }


    // Verificar disponibilidade de estoque
    const disponibilidade = await verificarDisponibilidadeEstoque(reserva.produtos, uid);
    if (!disponibilidade.disponivel) {
      return {
        success: false,
        error: `Produtos indisponíveis: ${disponibilidade.produtosIndisponiveis.join(', ')}`
      };
    }

    // Buscar dados do cliente na subcoleção do usuário
    const clienteDoc = await getDoc(doc(db, 'usuarios', uid, 'clientes', reserva.clienteId));
    const clienteData = clienteDoc.exists() ? clienteDoc.data() : {};

    // Calcular valor total se não fornecido
    const valorTotal = reserva.valorTotal || reserva.produtos.reduce((sum, p) => sum + (p.precoTotal || 0), 0);

    // Gerar número sequencial único da reserva
    const numeroResult = await generateSequentialReservaNumber();
    if (!numeroResult.success) {
      return { success: false, error: numeroResult.error };
    }
    const numeroReserva = numeroResult.data;

    const docRef = await addDoc(collection(db, 'reservas'), {
      ...reserva,
      numeroReserva,
      clienteNome: clienteData.nome || 'Cliente não encontrado',
      valorTotal,
      horaFinalizacao: new Date().toTimeString().split(' ')[0], // HH:MM:SS imutável
      createdAt: new Date()
    });

    // Atualizar estoque (diminuir quantidade)
    await atualizarEstoqueProdutos(reserva.produtos, 'reservar', uid);

    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateReserva = async (id: string, reserva: Partial<Reserva>): Promise<ApiResponse<void>> => {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    // Buscar reserva atual para comparar status
    const reservaDoc = await getDoc(doc(db, 'reservas', id));
    if (!reservaDoc.exists()) {
      return { success: false, error: 'Reserva não encontrada' };
    }

    const reservaAtual = reservaDoc.data() as Reserva;
    const novoStatus = reserva.status;

    // Se o status mudou para 'devolvido', devolver produtos ao estoque
    if (novoStatus === 'devolvido' && reservaAtual.status !== 'devolvido') {
      await atualizarEstoqueProdutos(reservaAtual.produtos, 'devolver', uid);
    }
    // Se o status mudou de 'devolvido' para outro status, reservar produtos novamente
    else if (reservaAtual.status === 'devolvido' && novoStatus && novoStatus !== 'devolvido') {
      const disponibilidade = await verificarDisponibilidadeEstoque(reservaAtual.produtos, uid);
      if (!disponibilidade.disponivel) {
        return {
          success: false,
          error: `Produtos indisponíveis para reserva: ${disponibilidade.produtosIndisponiveis.join(', ')}`
        };
      }
      await atualizarEstoqueProdutos(reservaAtual.produtos, 'reservar', uid);
    }

    await updateDoc(doc(db, 'reservas', id), reserva);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteReserva = async (id: string): Promise<ApiResponse<void>> => {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    // Buscar reserva antes de excluir para devolver produtos ao estoque
    const reservaDoc = await getDoc(doc(db, 'reservas', id));
    if (reservaDoc.exists()) {
      const reserva = reservaDoc.data() as Reserva;
      // Se a reserva não estava devolvida, devolver produtos ao estoque
      if (reserva.status !== 'devolvido') {
        await atualizarEstoqueProdutos(reserva.produtos, 'devolver', uid);
      }
    }

    await deleteDoc(doc(db, 'reservas', id));
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Função para buscar reserva por ID (pública)
export const getReservaById = async (id: string): Promise<ApiResponse<Reserva>> => {
  try {
    const reservaDoc = await getDoc(doc(db, 'reservas', id));

    if (!reservaDoc.exists()) {
      return { success: false, error: 'Reserva não encontrada' };
    }

    const reserva = { id: reservaDoc.id, ...reservaDoc.data() } as Reserva;
    return { success: true, data: reserva };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Busca todas as reservas de um cliente específico
 * @param userId - ID do usuário
 * @param clienteId - ID do cliente
 * @returns Lista de reservas do cliente ordenadas por data de retirada (mais recente primeiro)
 */
export const getReservasByCliente = async (
  userId: string,
  clienteId: string
): Promise<ApiResponse<Reserva[]>> => {
  try {
    const q = query(
      collection(db, 'reservas'),
      where('userId', '==', userId),
      where('clienteId', '==', clienteId),
      orderBy('dataRetirada', 'desc')
    );
    
    const snap = await getDocs(q);
    const reservas = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate() || new Date()
    } as Reserva));
    
    return { success: true, data: reservas };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Gera um número de reserva sequencial único usando transação do Firestore
 * Formato: RES-0001, RES-0002, etc.
 * Garante que não haverá duplicação mesmo com criações simultâneas
 */
export const generateSequentialReservaNumber = async (): Promise<ApiResponse<string>> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const result = await runTransaction(db, async (transaction) => {
      // Referência para o contador do usuário
      const counterRef = doc(db, 'reserva_counters', userId);

      // Ler o contador atual
      const counterDoc = await transaction.get(counterRef);

      let currentNumber: number;

      if (counterDoc.exists()) {
        // Se o contador existe, incrementar
        currentNumber = counterDoc.data().lastNumber + 1;
        transaction.update(counterRef, { lastNumber: currentNumber });
      } else {
        // Se não existe, criar com número 1
        currentNumber = 1;
        transaction.set(counterRef, {
          lastNumber: currentNumber,
          createdAt: new Date(),
          userId: userId
        });
      }

      // Formatar o número com zeros à esquerda (4 dígitos)
      const formattedNumber = `${currentNumber.toString().padStart(4, '0')}`;

      return formattedNumber;
    });

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Erro ao gerar número de reserva:', error);
    return { success: false, error: error.message || 'Erro ao gerar número de reserva' };
  }
};

/**
 * Busca o último número de reserva gerado para o usuário atual
 * Útil para verificação e debug
 */
export const getLastReservaNumber = async (): Promise<ApiResponse<number>> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const counterRef = doc(db, 'reserva_counters', userId);
    const counterDoc = await getDoc(counterRef);

    if (!counterDoc.exists()) {
      return { success: true, data: 0 };
    }

    const lastNumber = counterDoc.data().lastNumber || 0;
    return { success: true, data: lastNumber };
  } catch (error: any) {
    return { success: false, error: error.message || 'Erro ao buscar último número' };
  }
};

// Função otimizada para contar produtos sem carregar dados
export const getProdutosCount = async (userId: string): Promise<ApiResponse<number>> => {
  try {
    const q = query(
      collection(db, 'produtos'),
      where('userId', '==', userId)
    );
    const snap = await getDocs(q);

    // Só retorna o tamanho, não os dados
    return { success: true, data: snap.size };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Criar funções de contagem também para clientes e reservas
export const getReservasCount = async (userId: string): Promise<ApiResponse<number>> => {
  try {
    const q = query(
      collection(db, 'reservas'),
      where('userId', '==', userId)
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.size };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getReservasAtivasCount = async (userId: string): Promise<ApiResponse<number>> => {
  try {
    const q = query(
      collection(db, 'reservas'),
      where('userId', '==', userId),
      where('status', 'in', ['reservado', 'retirado'])
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.size };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getClientesCount = async (userId: string): Promise<ApiResponse<number>> => {
  try {
    const q = query(collection(db, 'usuarios', userId, 'clientes'));
    const snap = await getDocs(q);
    return { success: true, data: snap.size };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Funções para Devoluções
/**
 * Busca reservas por data de devolução
 * @param userId - ID do usuário
 * @param dataDevolucao - Data de devolução no formato YYYY-MM-DD
 * @returns Lista de reservas com devolução prevista para a data
 */
export const getReservasByDataDevolucao = async (
  userId: string,
  dataDevolucao: string
): Promise<ApiResponse<Reserva[]>> => {
  try {
    // Busca usando um intervalo para capturar datas que tenham horário (ex: 2026-01-13T...)
    const q = query(
      collection(db, 'reservas'),
      where('userId', '==', userId),
      where('dataDevolucao', '>=', dataDevolucao),
      where('dataDevolucao', '<=', dataDevolucao + '\uf8ff')
    );
    const snap = await getDocs(q);
    const reservas = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate() || new Date()
    } as Reserva));

    // Ordenar no cliente por nome do cliente
    reservas.sort((a, b) => (a.clienteNome || '').localeCompare(b.clienteNome || ''));

    return { success: true, data: reservas };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Atualiza o status de devolução de uma reserva
 * @param reservaId - ID da reserva
 * @param devolucaoRealizada - Se a devolução foi realizada
 * @param observacoes - Observações sobre a devolução
 * @returns Resultado da operação
 */
export const updateDevolucaoStatus = async (
  reservaId: string,
  devolucaoRealizada: boolean,
  observacoes?: string
): Promise<ApiResponse<void>> => {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    // Buscar reserva atual para verificar o status
    const reservaDoc = await getDoc(doc(db, 'reservas', reservaId));
    if (!reservaDoc.exists()) {
      return { success: false, error: 'Reserva não encontrada' };
    }

    const reservaAtual = reservaDoc.data() as Reserva;
    
    // Validar: só pode marcar como devolvido se o status for 'retirado'
    if (devolucaoRealizada && reservaAtual.status !== 'retirado') {
      return { 
        success: false, 
        error: 'Apenas reservas com status "Retirado" podem ser marcadas como devolvidas' 
      };
    }

    const updateData: any = {
      devolucaoRealizada,
      devolucaoObservacoes: observacoes || ''
    };

    // Se marcou como devolvido, registrar a data e atualizar status
    if (devolucaoRealizada) {
      updateData.dataDevolucaoRealizada = new Date().toISOString().split('T')[0];
      // Atualizar status para 'devolvido' (já validamos que o status atual é 'retirado')
      updateData.status = 'devolvido';
    } else {
      updateData.dataDevolucaoRealizada = null;
    }

    // Usar updateReserva se precisar atualizar o status (para devolver produtos ao estoque)
    if (devolucaoRealizada) {
      const resultUpdateReserva = await updateReserva(reservaId, updateData);
      if (!resultUpdateReserva.success) {
        return resultUpdateReserva;
      }
    } else {
      // Apenas atualizar os campos de devolução sem alterar o status
      await updateDoc(doc(db, 'reservas', reservaId), updateData);
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};